import React, {
  VoidFunctionComponent,
  ComponentProps,
  useState,
  useCallback
} from 'react';
import {
  ButtonStandard,
  ButtonStandardType
} from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Icon } from 'record-it-components/misc/Icon/Icon';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { EntityName } from 'record-it-time-common/EntityName';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { UserSelect, UserSelectProps } from '../../input/UserSelect/UserSelect';
import { UserGroupToUsersWidgetUser } from './UserGroupToUsersWidgetUser/UserGroupToUsersWidgetUser';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { UserGroupToUser } from '../../../classes/entities/UserGroupToUser/userGroupToUserTypes';
import styles from './UserGroupToUsersWidget.module.css';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';

export type UserGroupToUsersWidgetProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  userGroup: UserGroup;
};

export const UserGroupToUsersWidget: VoidFunctionComponent<UserGroupToUsersWidgetProps> =
  React.memo((props) => {
    const { userGroup, ...remainingProps } = prepareProps({ props });

    const [userIdToAdd, setUserIdToAdd] = useState<string | null>(null);
    const userGroupToUsers = useEntities(
      EntityName.UserGroupToUser,
      'getByUserGroupId',
      userGroup.id
    );
    const userGroupToUserRepository = useEntityRepository(
      EntityName.UserGroupToUser
    );

    const handleAddUserClick = useEventCallback(() => {
      assertNotNullOrUndefined(userIdToAdd, 'no userIdToAdd selected');

      userGroupToUserRepository.create({
        userId: userIdToAdd,
        ownerUserGroupId: userGroup.id
      });

      setUserIdToAdd(null);
    });

    const handleDelete = useEventCallback(
      (userGroupToUser: UserGroupToUser) => {
        userGroupToUserRepository.delete(userGroupToUser);
      }
    );

    const handleUserGroupUserChanged = useEventCallback(
      (modifiedUserGroupUser: UserGroupToUser) => {
        userGroupToUserRepository.update(modifiedUserGroupUser);
      }
    );

    const filterUsers: Required<UserSelectProps>['filterUsers'] = useCallback(
      (users) => {
        return users.filter(
          (u) => !userGroupToUsers.find((ugu) => ugu.userId === u.id)
        );
      },
      [userGroupToUsers]
    );

    return (
      <div {...remainingProps}>
        <div className={styles.userList}>
          {userGroupToUsers.map((userGroupToUser) => {
            return (
              <UserGroupToUsersWidgetUser
                key={userGroupToUser.userId}
                userGroupId={userGroup.id}
                userGroupToUser={userGroupToUser}
                onDelete={handleDelete}
                onUserGroupUserChanged={handleUserGroupUserChanged}
                data-testid="UserGroupToUsersWidget.User"
              ></UserGroupToUsersWidgetUser>
            );
          })}
        </div>

        <div className={styles.addUserWrapper}>
          <UserSelect
            label=""
            value={userIdToAdd}
            onValueChanged={setUserIdToAdd}
            filterUsers={filterUsers}
            data-testid="UserGroupToUsersWidget.UserSelect"
          ></UserSelect>
          <ButtonStandard
            type={ButtonStandardType.Primary}
            onClick={handleAddUserClick}
            className={styles.addUserButton}
            disabled={!userIdToAdd}
            data-testid="UserGroupToUsersWidget.AddUserButton"
          >
            <Icon name="plus-circle"></Icon>
          </ButtonStandard>
        </div>
      </div>
    );
  });
